import { useRouter } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { LiveEvents } from '../features/live/LiveEvents'
import { LiveToast } from '../features/live/LiveToast'
import { useQueueUnread } from '../features/queue/queries'
import { ChannelHealthBanner } from '../features/settings/ChannelHealthBanner'
import { OutageScreen } from '../features/status/OutageScreen'
import { ReadyzProbe } from '../features/status/ReadyzProbe'
import { ServiceStatusBar } from '../features/status/ServiceStatusBar'
import { useServiceStatus } from '../features/status/useServiceStatus'
import { useOpenPathsFromPush } from '../pwa/pushNavigation'
import { useSession } from '../session/useSession'
import type { SessionValue } from '../session/SessionProvider'
import { AiPausedBanner } from './AiPausedBanner'
import { SHELL_COPY } from './copy'
import { NavBar } from './NavBar'
import { useShellLiveAlerts, type ShellLiveAlerts } from './useShellLiveAlerts'
import { useUnreadTitle } from './useUnreadTitle'

type ReadySession = Extract<SessionValue, { status: 'ready' }>

const ReadyShell = ({ session, alerts, children }: { session: ReadySession; alerts: ShellLiveAlerts; children: ReactNode }) => {
  const { data: unreadSummary } = useQueueUnread()
  const queueUnread = unreadSummary?.conversations ?? 0
  useUnreadTitle(queueUnread)
  const router = useRouter()
  useOpenPathsFromPush(session.membership.tenantId, (href) => void router.navigate({ href }))
  const displayName = session.me.user.displayName

  return (
    <div className="shell">
      <a className="skip-link" href="#main">
        {SHELL_COPY.skipToMain}
      </a>
      <ReadyzProbe api={session.api} />
      <div className="shell-status">
        <ServiceStatusBar />
        <AiPausedBanner />
        <ChannelHealthBanner />
        <LiveEvents onInbox={alerts.onInbox} />
      </div>
      <NavBar
        role={session.membership.role}
        tenantName={session.membership.tenantName}
        queueUnread={queueUnread}
        {...(displayName === '' ? {} : { userName: displayName })}
        {...(session.me.multiTenantEnabled ? { onSwitchTenant: session.clearTenant } : {})}
        onSignOut={() => void session.signOut()}
      />
      <main id="main" className="shell-main" tabIndex={-1}>
        {children}
      </main>
      <LiveToast toast={alerts.toast} onDismiss={alerts.dismissToast} onOpen={alerts.openItem} />
    </div>
  )
}

export const AppShell = ({ children }: { children: ReactNode }) => {
  const session = useSession()
  const { outage, lastSyncedAt } = useServiceStatus()
  const alerts = useShellLiveAlerts()
  if (session.status !== 'ready') return null
  if (outage !== null) return <OutageScreen entry={outage} lastSyncedAt={lastSyncedAt} />

  return (
    <ReadyShell session={session} alerts={alerts}>
      {children}
    </ReadyShell>
  )
}
